import { useCallback, useEffect, useState } from "react";

/**
 * Lifecycle hook for the Smplify push-only service worker.
 *
 * What this hook DOES:
 * - Registers `/sw-push.js` (no caching, push-only — see the file header).
 * - Tracks the current `Notification.permission` value.
 * - Exposes `request()` to prompt the user for permission.
 * - Exposes `subscribe()` / `unsubscribe()` for Web Push subscriptions.
 * - Exposes `showLocal()` to fire a notification via the active SW for
 *   demo/testing without any backend involvement.
 *
 * What this hook does NOT do:
 * - It does not send subscriptions to a backend. Real remote push delivery
 *   needs (1) a VAPID keypair, (2) a backend endpoint that stores the
 *   PushSubscription per user, and (3) a sender that calls the Web Push
 *   protocol. Pass the public VAPID key via `vapidPublicKey` once that
 *   backend exists; until then, `subscribe()` no-ops and we rely on
 *   `showLocal()` for in-app notifications.
 *
 * Guards: skipped entirely when running inside an iframe or on Lovable
 * preview hosts so the editor preview never registers a worker.
 */

const isInsideIframe = () => {
  try {
    return typeof window !== "undefined" && window.self !== window.top;
  } catch {
    return true;
  }
};

const isLovablePreviewHost = () => {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h.includes("id-preview--") || h.includes("lovableproject.com");
};

const isSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "Notification" in window &&
  "PushManager" in window;

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const base64Url = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64Url);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export type PushPermission = "default" | "granted" | "denied" | "unsupported";

export interface UsePushNotificationsOptions {
  /** Public VAPID key from your backend. Required for real remote push. */
  vapidPublicKey?: string;
}

export function usePushNotifications(options: UsePushNotificationsOptions = {}) {
  const supported = isSupported() && !isInsideIframe() && !isLovablePreviewHost();
  const [permission, setPermission] = useState<PushPermission>(() => {
    if (!isSupported()) return "unsupported";
    return Notification.permission as PushPermission;
  });
  const [registration, setRegistration] =
    useState<ServiceWorkerRegistration | null>(null);
  const [subscription, setSubscription] = useState<PushSubscription | null>(null);

  // Register the push-only service worker on mount.
  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    navigator.serviceWorker
      .register("/sw-push.js", { scope: "/" })
      .then(async (reg) => {
        if (cancelled) return;
        setRegistration(reg);
        const existing = await reg.pushManager.getSubscription();
        if (!cancelled) setSubscription(existing);
      })
      .catch((err) => {
        // Don't toast — the rest of the app must keep working if SW registration fails.
        console.warn("[push] service worker registration failed", err);
      });
    return () => {
      cancelled = true;
    };
  }, [supported]);

  const request = useCallback(async (): Promise<PushPermission> => {
    if (!supported) return "unsupported";
    const result = await Notification.requestPermission();
    setPermission(result as PushPermission);
    return result as PushPermission;
  }, [supported]);

  const subscribe = useCallback(async (): Promise<PushSubscription | null> => {
    if (!supported || !registration) return null;
    if (Notification.permission !== "granted") {
      const granted = await request();
      if (granted !== "granted") return null;
    }
    if (!options.vapidPublicKey) {
      // Without VAPID we can't create a server-bound subscription. Surface
      // this clearly in dev so engineers know what's missing.
      console.warn(
        "[push] subscribe() called without a VAPID public key — skipping. " +
          "Wire up the backend push sender and pass vapidPublicKey to enable."
      );
      return null;
    }
    const sub = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(options.vapidPublicKey).buffer as ArrayBuffer,
    });
    setSubscription(sub);
    return sub;
  }, [supported, registration, request, options.vapidPublicKey]);

  const unsubscribe = useCallback(async (): Promise<boolean> => {
    if (!subscription) return true;
    const ok = await subscription.unsubscribe();
    if (ok) setSubscription(null);
    return ok;
  }, [subscription]);

  /**
   * Fire a notification via the active service worker without involving any
   * backend — useful for the demo and for local QA of the worker plumbing.
   */
  const showLocal = useCallback(
    async (title: string, body?: string, url?: string) => {
      if (!supported || !registration) return false;
      if (Notification.permission !== "granted") {
        const granted = await request();
        if (granted !== "granted") return false;
      }
      await registration.showNotification(title, {
        body: body ?? "",
        icon: "/icons/icon-512.png",
        badge: "/icons/icon-512.png",
        data: { url: url ?? "/" },
      });
      return true;
    },
    [supported, registration, request]
  );

  return {
    supported,
    permission,
    isSubscribed: Boolean(subscription),
    request,
    subscribe,
    unsubscribe,
    showLocal,
  };
}
